import { FC, useMemo } from "react";

import { CreateGUFNewLicenceTask, EditGUFNewLicenceTask } from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GufNewLicenceFormProps } from "@/types/form-types";

import { sanitizeGufEnergyCharacteristicsForSubmit } from "../schema/guf-installation-function-helpers";
import { getGufNewLicenceSchema } from "../schema/guf-new-license-schema";
import { useGufNewLicenceUiSchema } from "../schema/guf-new-license-ui-schema";
import { validateGufNewLicenceForm } from "../schema/guf-validation";

const GufNewLicenceForm: FC<GufNewLicenceFormProps> = (props) => {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();

  const { oldUploadTask, task, type } = getFormObjectValues(props);
  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    CreateGUFNewLicenceTask | EditGUFNewLicenceTask
  >({
    key: "guf-new-licence",
    task,
  });

  const uiSchema = useGufNewLicenceUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(() => getGufNewLicenceSchema({ type, isEditing }), [type, isEditing]);

  const onSubmit = async () => {
    if (!formData) return;
    const sanitizedFormData = sanitizeGufEnergyCharacteristicsForSubmit(formData);
    if (getIsEditing(sanitizedFormData)) {
      await api.editGUFNewLicence(sanitizedFormData);
    } else {
      await api.createGUFNewLicence(sanitizedFormData);
    }
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });
    await afterSubmit();
  };

  return (
    <BrostarForm<CreateGUFNewLicenceTask | EditGUFNewLicenceTask>
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={uiSchema}
      onSubmit={onSubmit}
      customValidate={validateGufNewLicenceForm}
    />
  );
};

export { GufNewLicenceForm };
